import { cn } from "@/lib/utils";
import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/components/ui/avatar";
import { useDispatch, useSelector } from "@/core/redux/store";
import {
  IInbox,
  InboxType,
} from "@/shared/services/interface/responses/IInboxResponses";
import {
  GetExploreCompletedCount,
  GetInboxList,
  GetInboxListIcon,
  GetInboxListTexts,
  IInboxListPartial,
  TimeCalculator,
} from "../helper/list-item-helper";
import { useTranslation } from "react-i18next";
import { setSelectedInbox } from "@/core/redux/slices/inbox";
import { getActivities, markAsRead } from "@/core/redux/thunks/inboxThunk";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Inbox } from "lucide-react";
import { Badge } from "@/shared/components/ui/badge";
import { trackNotificationClicked } from "@/lib/rum-actions";

export function MailList() {
  const { t } = useTranslation();
  const inbox = useSelector((state) => state.inbox.inbox);
  const isLoading = useSelector((state) => state.inbox.inboxLoading);
  const [firstLoad, setFirstLoad] = useState(true);
  const [inboxList, setInboxList] = useState<IInboxListPartial>({
    new: [],
    today: [],
    yesterday: [],
    old: [],
  });
  const dispatch = useDispatch();
  const selectedInbox = useSelector((state) => state.inbox.selectedInbox);
  const [avatar, setAvatar] = useState<string | null>(
    "https://github.com/shadcn.png"
  );
  const me = useSelector((state) => state.user.me);
  useEffect(() => {
    const response = GetInboxList(inbox.data);
    setInboxList(response);
    setFirstLoad(false);
    setAvatar("");
  }, [inbox, inbox.data]);

  const AvatarAndIcon = ({
    mainIcon,
    subIcon,
  }: {
    mainIcon: React.ReactNode;
    subIcon: React.ReactNode;
  }) => {
    return (
      <div className="flex flex-row gap-2">
        {!avatar ? (
          <div className=" w-[33px] h-[33px] rounded-full  border border-zinc-200 flex items-center justify-center relative">
            {mainIcon}
            <div
              className="w-[19px] h-[19px] rounded-full bg-zinc-100 border border-white flex items-center justify-center absolute"
              style={{
                position: "absolute",
                top: "18px",
                right: "-8px",
              }}
            >
              {subIcon}
            </div>
          </div>
        ) : (
          <Avatar className="w-[33px] h-[33px] overflow-visible">
            <AvatarImage src={""} />
            <AvatarFallback className="relative border border-zinc-200">
              {mainIcon}
              <div
                className="w-[19px] h-[19px] rounded-full bg-zinc-100 border border-white flex items-center justify-center absolute"
                style={{
                  position: "absolute",
                  top: "18px",
                  right: "-8px",
                }}
              >
                {subIcon}
              </div>
            </AvatarFallback>
          </Avatar>
        )}
      </div>
    );
  };

  const handleClick = (inboxItem: IInbox) => {
    if (!me?.id) return;
    dispatch(setSelectedInbox(inboxItem));
    dispatch(getActivities({ userId: me?.id, targetId: inboxItem.targetId }));

    // RUM-035: Track notification clicked
    const receivedTime = new Date(inboxItem.createdAt).getTime();
    const timeToClickMs = Date.now() - receivedTime;
    trackNotificationClicked(inboxItem.id, inboxItem.type, timeToClickMs);

    if (!inboxItem.isRead) {
      dispatch(markAsRead(inboxItem.id));
    }
  };

  const InboxItem = ({ item }: { item: IInbox }) => {
    const me = useSelector((state) => state.user.me);
    const { mainIcon, subIcon } = GetInboxListIcon(item.type);
    const { title, description, subDescription } = GetInboxListTexts(
      item.type,
      item.metadata || {},
      t,
      me?.id === item?.metadata?.sendedBy?.id
    );

    const isExploreShow =
      item?.type ===
      InboxType.SPECIFICATION_EXPLORE_MATCHED_PRODUCT_COMPLETED ||
      item?.type ===
      InboxType.SPECIFICATION_EXPLORE_USER_AND_COMMENT_COMPLETED ||
      item?.type ===
      InboxType.SPECIFICATION_EXPLORE_LOCAL_PARTNER_SUPPORT_COMPLETED ||
      item?.type ===
      InboxType.SPECIFICATION_EXPLORE_COMPETITIVE_INDEX_COMPLETED ||
      item?.type === InboxType.SPECIFICATION_EXPLORE_PRODUCER_SCORE_COMPLETED;

    return (
      <button
        key={item.id}
        className={cn(
          "flex flex-col items-start gap-2 rounded-lg border p-3 text-left text-sm transition-all hover:bg-accent",
          selectedInbox?.id === item.id && "bg-muted"
        )}
        onClick={() => handleClick(item)}
      >
        <div className="flex flex-row gap-3 w-full">
          <AvatarAndIcon mainIcon={mainIcon} subIcon={subIcon} />
          <div className="flex flex-col w-full">
            <div className="flex w-full flex-col gap-1">
              <div className="flex items-center w-full">
                <div className="flex items-center gap-2 justify-between w-[100%]">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-600 text-[14px] mt-1">{title}</span>
                    {isExploreShow && (
                      <Badge
                        variant="secondary"
                        className=" !w-[55px] !text-center bg-green-50 !p-0 h-[17px] hover:bg-green-50"
                        style={{
                          border: "1px solid rgba(22, 163, 74, 0.20)",
                        }}
                      >
                        <span className="text-xs text-center font-600 text-green-600 w-full">
                          {GetExploreCompletedCount(item.type)} / 5
                        </span>
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {!item.isRead && (
                      <>
                        <span className="text-[11px] font-500 text-muted-foreground">
                          {TimeCalculator(
                            item.updatedAt ? item.updatedAt : item.createdAt,
                            t
                          )}
                        </span>
                        <span className="flex h-2 w-2 rounded-full bg-green-500" />
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div
                className="text-[12px] font-500 text-zinc-950 -mt-1"
                dangerouslySetInnerHTML={{ __html: description || "" }}
              />
            </div>
            <div className="text-[12px] text-muted-foreground font-500">
              {subDescription}
            </div>
          </div>
        </div>
      </button>
    );
  };

  const InboxItemSkeleton = ({ index }: { index: number }) => {
    return (
      <button
        key={index}
        className={cn(
          "flex flex-col items-start gap-2 rounded-lg border p-3 text-left text-sm transition-all hover:bg-accent"
        )}
      >
        <div className="flex flex-row gap-3 w-full">
          <Skeleton className="w-10 h-10 rounded-full" />
          <div className="flex flex-col w-full">
            <div className="flex w-full flex-col gap-1">
              <div className="flex items-center w-full">
                <div className="flex items-center gap-2 justify-between w-[100%]">
                  <Skeleton className="w-[100px] h-[14px]" />
                  <div className="flex items-center gap-1"></div>
                </div>
              </div>
              <div className="text-[12px] font-500 text-zinc-950 -mt-1">
                <Skeleton className="w-[100px] h-[14px]" />
              </div>
            </div>
            <div className="line-clamp-2 text-xs text-muted-foreground">
              <Skeleton className="w-[100px] h-[14px]" />
            </div>
          </div>
        </div>
      </button>
    );
  };

  const InboxDateList = ({
    inboxItems,
    title,
  }: {
    inboxItems: IInbox[];
    title?: string;
  }) => {
    if (isLoading && firstLoad) {
      return (
        <div className="flex flex-col gap-2">
          {title && (
            <div className="text-[12px] font-500 text-zinc-500">{title}</div>
          )}

          <div className="flex flex-col gap-2">
            {Array.from({ length: 5 }).map((_, index) => (
              <InboxItemSkeleton key={index} index={index} />
            ))}
          </div>
        </div>
      );
    }
    if (inboxItems.length === 0) return null;
    return (
      <div className="flex flex-col gap-2">
        {title && (
          <div className="text-[12px] font-500 text-zinc-500">{title}</div>
        )}
        {inboxItems?.map((item) => {
          return <InboxItem key={item.id} item={item} />;
        })}
      </div>
    );
  };

  const inboxListComputed = useMemo(() => {
    const inboxListComputed_ = {} as IInboxListPartial
    Object.keys(inboxList).forEach((key) => {
      inboxListComputed_[key as keyof IInboxListPartial] = inboxList[key as keyof IInboxListPartial].filter((item) => {
        return item.metadata
      });
    });
    return inboxListComputed_;
  }, [inboxList]);

  return (
    <div className="overflow-y-auto scrollbar-hide h-[82vh]">
      <div className="flex flex-col gap-2 p-4 pt-0 mt-2">
        <InboxDateList inboxItems={inboxListComputed.new} title={t("new")} />
        <InboxDateList inboxItems={inboxListComputed.today} title={t("today")} />
        <InboxDateList
          inboxItems={inboxListComputed.yesterday}
          title={t("yesterday")}
        />
        <InboxDateList inboxItems={inboxListComputed.old} title={t("inbox:older")} />
        {inbox.data.length == 0 && (
          <Card className="flex flex-col gap-2 p-4">
            <CardContent className="flex flex-col gap-2 items-center justify-center p-0">
              <span className="text-center">
                <Inbox className="!w-6 !h-6" />
              </span>
              <span className="text-center">{t("inbox:empty_inbox")}</span>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
